import {
  BoxProps,
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  FormGroup,
  styled,
} from "@mui/material";
import { Box } from "@mui/system";
import isEqual from "lodash/isEqual";
import { memo, useCallback, useEffect, useState } from "react";
import useDebounce from "../../../utils/useDebounce";
import { FilterViewProps } from "./types";

interface StyledBoxProps extends BoxProps {
  emphasize: boolean;
}

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "emphasize",
})<StyledBoxProps>(({ theme, emphasize }) => ({
  border: `1px solid ${theme.palette.divider}`,
  width: "100%",
  height: "100%",
  position: "absolute",
  borderRadius: "4px",
  left: 0,
  top: 0,
  "&:hover": {
    transition: theme.transitions.create(["border"]),
    borderColor: emphasize ? theme.palette.info.main : theme.palette.primary.main,
  },
  ...(emphasize && {
    transition: theme.transitions.create(["border"]),
    borderColor: theme.palette.info.main,
    borderWidth: "2px",
    "&:hover fieldset": {
      borderColor: theme.palette.info.main,
    },
    "&.Mui-focused fieldset": {
      transition: theme.transitions.create(["border"]),
      borderColor: theme.palette.info.main,
    },
  }),
}));

const values = ["true", "false"];

function mapBooleanValue(value: string | null): "true" | "false" | null {
  switch (value) {
    case "true": // fallthrough
    case "false":
      return value;
    default:
      return null;
  }
}

const BooleanFilter = memo(
  ({
    filterId,
    filterValue,
    urlSearchParam,
    labels,
    setFilterState,
    hideFalse,
    hideTrue,
  }: FilterViewProps & {
    labels: [string, string];
    hideFalse?: boolean;
    hideTrue?: boolean;
  }) => {
    const [currentValue, setValue] = useState(mapBooleanValue(urlSearchParam));
    const debouncedValue = useDebounce(currentValue, 50);

    const handleChange = useCallback(
      ({ target: { name, checked } }: React.ChangeEvent<HTMLInputElement>) => {
        setValue(mapBooleanValue(checked ? name : null));
      },
      []
    );

    useEffect(() => {
      setFilterState(filterId, { value: debouncedValue }, debouncedValue);
    }, [debouncedValue, filterId, setFilterState]);

    // for clearing the filter
    useEffect(() => {
      setValue(mapBooleanValue(urlSearchParam));
    }, [urlSearchParam]);

    let hideIndex: number | undefined;

    if (hideTrue) {
      hideIndex = 0;
    }

    if (hideFalse) {
      hideIndex = 1;
    }

    return (
      <FormGroup
        sx={{
          position: "relative",
          flexDirection: "row",
          justifyContent: "space-between",
          alignSelf: "center",
          m: 1,
          px: "14px",
          py: "7px",
          minWidth: "200px",
          backgroundColor: (theme) => theme.palette.common.white,
        }}
      >
        {labels.map((label, i) => {
          const FormCheckBox = (
            <FormControlLabel
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              sx={{
                color: (theme) => theme.palette.primary.main,
                [`& .${checkboxClasses.root}`]: { p: 0, mx: 1 },
                "&:hover ~ #styledBox": {
                  borderColor: filterValue ? "info.main" : "primary.main",
                },
              }}
              control={
                <Checkbox
                  sx={{ pr: 1 }}
                  name={values[i]}
                  checked={currentValue === values[i]}
                  onChange={handleChange}
                />
              }
              label={label}
            />
          );
          return hideIndex !== undefined ? (i === hideIndex ? null : FormCheckBox) : FormCheckBox;
        })}
        <StyledBox id="styledBox" emphasize={!!filterValue} />
      </FormGroup>
    );
  },
  (prev, next) => {
    const keys = Object.keys(prev) as (keyof typeof prev)[];
    if (keys.length !== Object.keys(next).length) {
      return false;
    }
    for (const key of keys) {
      if (key === "labels") {
        if (prev.labels[0] !== next.labels[0] || prev.labels[1] !== next.labels[1]) {
          return false;
        }
      } else if (!isEqual(prev[key], next[key])) {
        return false;
      }
    }
    return true;
  }
);

export default BooleanFilter;
